import debounce from "lodash.debounce";
import { useInterval } from "react-use";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts";
import { useAppDispatch, useAppSelector } from "@/stores";
import { DateHelper, FileHelper, FormHelper } from "@/helpers";
import { DateRange } from "@/domains/common";
import { UserCategory, UserService } from "@/domains/user";
import {
  TransferCategory,
  TransferQueryParams,
  TransferDepositStatusSearchOptions,
  TransferService,
  TransferQueryFormData,
} from "@/domains/transfer";
import {
  operatorTenantDepositActions,
  operatorTenantDepositSelects,
} from "./store";
// components
import { Button, DatePicker, Form, Input, Select } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import {
  DateRangeRadioGroup,
  DateRangeRadioGroupChangeEvent,
} from "@/components";

type TransferQueryParamsFormValues = QueryParamsFormValues<TransferQueryParams>;

export const TableTopFilters = () => {
  const auth = useAuth();

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [form] = Form.useForm<TransferQueryFormData>();

  const queryParams = useAppSelector(operatorTenantDepositSelects.queryParams);

  const dateRange = useAppSelector(operatorTenantDepositSelects.dateRange);

  const [belongs, setBelongs] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    const queryParamsFormValues = {
      ...DateHelper.convertStringToDayjs(queryParams),
      ...FormHelper.getQueryParamsDateTypeRange(queryParams),
    };

    form.setFieldsValue({
      ...queryParamsFormValues,
      belong: queryParamsFormValues.belong
        ? queryParamsFormValues.belong.split(",")
        : [],
      status: queryParamsFormValues.status
        ? queryParamsFormValues.status.split(",").map(Number)
        : [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const search = debounce((values: TransferQueryParamsFormValues) => {
    const newQueryParams = DateHelper.convertDayjsToString(values);
    dispatch(operatorTenantDepositActions.setQueryParams(newQueryParams));
  }, 400);

  const convertToParamsForm = (values: TransferQueryFormData) => {
    const formValues = {
      ...values,
      [`${values.dateType}_started_at`]: values.dateRange[0],
      [`${values.dateType}_stopped_at`]: values.dateRange[1],
      belong: values.belong?.join(","),
      status: values.status?.join(","),
      dateType: undefined,
      dateRange: undefined,
    };

    return formValues satisfies TransferQueryParamsFormValues;
  };

  const handleValuesChange = (_: any, values: TransferQueryFormData) => {
    search(convertToParamsForm(values));
  };

  const handleDownload = async () => {
    const res = await TransferService.download({
      ...queryParams,
      category: TransferCategory.TenantDeposit,
    }).run();

    FileHelper.download({ data: res.data });
  };

  const loopGetMemberDepositTransfers = () => {
    if (import.meta.env.VITE_DISABLE_LONG_POLLING === "true") return;

    const formValue = form.getFieldsValue();
    search(convertToParamsForm(formValue));
  };

  const handleDateRangeRadioGroupChange: DateRangeRadioGroupChangeEvent = (
    value,
    getDateRange
  ) => {
    dispatch(operatorTenantDepositActions.setDateRange(value));
    if (!getDateRange) return;

    const dateRange = getDateRange();
    form.setFieldsValue({
      ...form.getFieldsValue,
      dateRange: dateRange,
    });

    handleValuesChange(null, form.getFieldsValue());
  };

  const handleDateRangeChange = () => {
    dispatch(operatorTenantDepositActions.setDateRange(DateRange.custom));
  };

  useInterval(loopGetMemberDepositTransfers, 10000);

  useEffect(() => {
    void (async () => {
      if (!auth.user) return;

      const isAccount = auth.user.category === UserCategory.TenantAccount;

      const parentId = isAccount ? auth.user.id : auth.user.parent_id!;

      const req = UserService.list({
        parent_id: parentId,
        category: UserCategory.TenantAccount,
      });

      const cachedRes = req.getCachedResponse();
      cachedRes && setBelongs(cachedRes.data.msg.users);

      const res = await req.run({ cached: true });
      setBelongs(res.data.msg.users);
    })();
  }, [auth.user]);

  return (
    <Form
      form={form}
      labelCol={{ className: "w:80" }}
      onValuesChange={handleValuesChange}
    >
      <div className="flex:col gap:16">
        <div className="flex ai:center gap:16">
          <Form.Item
            label={t("domains.Transfer.belongTarget")}
            name="belong"
            className="flex:1"
          >
            <Select
              allowClear
              mode="multiple"
              placeholder={t("systems.Placeholder.all")}
              maxTagCount="responsive"
              options={belongs.map((belong) => ({
                label: belong.name,
                value: belong.id,
              }))}
            ></Select>
          </Form.Item>
          <Form.Item
            label={t("domains.Transfer.orderId")}
            name="order"
            className="flex:1"
          >
            <Input
              allowClear
              placeholder={t("systems.Placeholder.allowFuzzySearch")}
            />
          </Form.Item>
          <Form.Item
            label={t("domains.Transfer.member")}
            name="member"
            className="flex:1"
          >
            <Input
              allowClear
              placeholder={t("systems.Placeholder.allowFuzzySearch")}
            />
          </Form.Item>
          <div className="mb:16 flex:1 flex ai:center jc:end">
            <Button
              type="primary"
              htmlType="button"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload()}
            >
              {t("systems.Action.downloadReport")}
            </Button>
          </div>
        </div>

        <div className="flex ai:center gap:16">
          <Form.Item
            label={t("domains.Transfer.systemId")}
            name="id"
            className="flex:1"
          >
            <Input
              allowClear
              placeholder={t("systems.Placeholder.allowFuzzySearch")}
            />
          </Form.Item>
          <Form.Item
            label={t("domains.Transfer.sourceAddress")}
            name="source"
            className="flex:1"
          >
            <Input
              allowClear
              placeholder={t("systems.Placeholder.allowFuzzySearch")}
            />
          </Form.Item>
          <Form.Item
            label={t("domains.Transfer.targetAddress")}
            name="target"
            className="flex:1"
          >
            <Input
              allowClear
              placeholder={t("systems.Placeholder.allowFuzzySearch")}
            />
          </Form.Item>
          <div className="flex:1" />
        </div>

        <div className="flex ai:center gap:16">
          <Form.Item
            label={t("domains.Transfer.transactionNote")}
            name="note"
            className="flex:1"
          >
            <Input
              allowClear
              placeholder={t("systems.Placeholder.allowFuzzySearch")}
            />
          </Form.Item>
          <Form.Item
            label={t("domains.Transfer.transactionHash")}
            name="transaction"
            className="flex:1"
          >
            <Input
              allowClear
              placeholder={t("systems.Placeholder.allowFuzzySearch")}
            />
          </Form.Item>
          <Form.Item
            label={t("domains.Transfer.transactionStatus")}
            name="status"
            className="flex:1"
          >
            <Select
              allowClear
              mode="multiple"
              placeholder={t("systems.Placeholder.all")}
              maxTagCount="responsive"
              options={TransferDepositStatusSearchOptions.map((opt) => ({
                label: t(opt.i18nKey),
                value: opt.value,
              }))}
            ></Select>
          </Form.Item>
          <div className="flex:1" />
        </div>

        <div className="flex ai:center gap:16">
          <Form.Item
            label={t("systems.Date.dateType")}
            name="dateType"
            className="flex:1"
          >
            <Select
              allowClear
              options={[
                {
                  label: t("domains.Transfer.depositCreatedAt"),
                  value: "created",
                },
                { label: t("domains.Transfer.checkedAt"), value: "checked" },
                { label: t("domains.Transfer.arrivedAt"), value: "arrived" },
              ]}
            ></Select>
          </Form.Item>
          <Form.Item
            label={t("systems.Date.dateRange")}
            name="dateRange"
            className="flex:1"
          >
            <DatePicker.RangePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              onChange={handleDateRangeChange}
            />
          </Form.Item>
          <div className="mb:16 flex:1 flex ai:center jc:end">
            <DateRangeRadioGroup
              dateRange={dateRange}
              onChange={handleDateRangeRadioGroupChange}
            />
          </div>
          <div className="flex:1" />
        </div>
      </div>
    </Form>
  );
};
